/**
 * Google Analytics 4 helpers with GDPR consent gating.
 *
 * Behavior:
 *  - gtag.js is loaded in index.html with default analytics_storage = 'denied'
 *    via Google Consent Mode v2.
 *  - Once the user accepts analytics cookies through the cookie banner, we
 *    call gtag('consent', 'update', { analytics_storage: 'granted' }) and
 *    enable real tracking.
 *  - All helper functions are no-ops if gtag is unavailable, so it is safe
 *    to call them from anywhere in the app.
 */

export const GA_MEASUREMENT_ID = "G-9VZDRNSD57";
export const CONSENT_STORAGE_KEY = "nalise_cookie_consent";

const safeGtag = (...args) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag(...args);
  } catch (e) {
    // ignore tracking errors silently
  }
};

/**
 * Returns the stored consent state, or null if no decision yet.
 * Shape: { status: 'accepted' | 'rejected' | 'partial', analytics: boolean, date }
 */
export const getStoredConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const hasAnalyticsConsent = () => {
  const c = getStoredConsent();
  return !!(c && c.analytics === true);
};

export const updateAnalyticsConsent = (granted) => {
  safeGtag("consent", "update", {
    analytics_storage: granted ? "granted" : "denied",
  });
};

export const setStoredConsent = (next) => {
  const payload = {
    status: next.status,
    analytics: !!next.analytics,
    date: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
  updateAnalyticsConsent(payload.analytics);
};

export const trackPageView = (path, title) => {
  if (!hasAnalyticsConsent()) return;
  safeGtag("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
};

/* ---------- E-commerce events (GA4 Enhanced Ecommerce) ---------- */

const toGaItem = (product, quantity = 1) => ({
  item_id: String(product.id),
  item_name: product.name,
  price: parseFloat(product.price || product.regular_price || 0),
  item_brand: product.brands?.[0]?.name || undefined,
  item_category: product.categories?.[0]?.name || undefined,
  quantity,
});

export const trackViewItem = (product) => {
  if (!hasAnalyticsConsent()) return;
  const item = toGaItem(product, 1);
  safeGtag("event", "view_item", {
    currency: "EUR",
    value: item.price,
    items: [item],
  });
};

export const trackAddToCart = (product, quantity = 1) => {
  if (!hasAnalyticsConsent()) return;
  const item = toGaItem(product, quantity);
  safeGtag("event", "add_to_cart", {
    currency: "EUR",
    value: item.price * quantity,
    items: [item],
  });
};

export const trackBeginCheckout = (cart, subtotal) => {
  if (!hasAnalyticsConsent()) return;
  safeGtag("event", "begin_checkout", {
    currency: "EUR",
    value: subtotal,
    items: cart.map((it) => ({
      item_id: String(it.id),
      item_name: it.name,
      price: parseFloat(it.price || 0),
      quantity: it.quantity,
    })),
  });
};

/**
 * Purchase event.
 * NOTE: the final "purchase" happens on the WooCommerce side after the
 * customer pays via PayPal. We fire this event right before redirecting
 * to the WooCommerce checkout URL so the funnel has a reliable signal.
 * For 100% accurate purchase tracking install GA4 on the WordPress side
 * as well (Site Kit by Google or GA Google Analytics plugin).
 */
export const trackPurchaseIntent = (transactionId, cart, total) => {
  if (!hasAnalyticsConsent()) return;
  safeGtag("event", "purchase", {
    transaction_id: transactionId,
    currency: "EUR",
    value: total,
    items: cart.map((it) => ({
      item_id: String(it.id),
      item_name: it.name,
      price: parseFloat(it.price || 0),
      quantity: it.quantity,
    })),
  });
};
